import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, UserCheck2 } from "lucide-react";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";
import { queryClient } from "@/ajax/queryClient";
import { MarkNotHiredDialog } from "@/features/affiliation/mark-not-hired-dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProfileAvatarImage } from "@/features/profile/components/profile-avatar-image";

function deriveNameParts(row: {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}) {
  const initials = [row.firstName, row.lastName]
    .filter(Boolean)
    .map((n) => n!.charAt(0).toUpperCase())
    .join("");
  const fullName =
    [row.firstName, row.lastName].filter(Boolean).join(" ") ||
    row.email ||
    "Unnamed provider";
  return { initials, fullName };
}

function AffiliatedProvidersPage() {
  const { user } = Route.useRouteContext();
  const { hasPermission } = useRolePermissions(user);
  const { selectedOrgBusinessUnit } = useOrganization();
  const trpc = useTRPC();

  const [endTarget, setEndTarget] = useState<{
    userId: string;
    name: string;
  } | null>(null);

  // Viewing the hired-providers roster is open to any BU member; only the
  // "mark not hired" action requires ManageAffiliations.
  const canManage = hasPermission(Permission.ManageAffiliations);

  const { data: affiliations, isLoading } = useQuery(
    trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryOptions({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
  );

  const markNotHiredMutate = useMutation(
    trpc.organizationBusinessUnitAffiliation.markNotHired.mutationOptions({
      onSuccess: async () => {
        setEndTarget(null);
        await queryClient.invalidateQueries({
          queryKey:
            trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryKey(
              {
                organizationBusinessUnitId: selectedOrgBusinessUnit.id,
              },
            ),
        });
        await queryClient.invalidateQueries({
          queryKey: ["profileListInfinite"],
        });
        toast.success("Provider marked not hired.");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to mark provider not hired.");
      },
    }),
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8 space-y-4 sm:space-y-6">
        {/* Page header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-slate-900">Hired Providers</h1>
          <p className="text-slate-600 text-sm sm:text-base">
            Providers currently hired by this organization.
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <Card className="border-dashed">
            <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 p-3 rounded-full mb-4">
                <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Loading hired providers
              </h3>
              <p className="text-gray-600 max-w-md text-sm sm:text-base">
                Please wait while we fetch the hired providers for this business
                unit.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!isLoading &&
          affiliations !== undefined &&
          affiliations.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center">
                <div className="bg-blue-100 p-3 rounded-full mb-4">
                  <UserCheck2 className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  No hired providers yet
                </h3>
                <p className="text-gray-600 max-w-md text-sm sm:text-base">
                  Providers hired by this organization will appear here.
                </p>
              </CardContent>
            </Card>
          )}

        {/* Desktop table + mobile cards */}
        {!isLoading && affiliations && affiliations.length > 0 && (
          <>
            {/* Mobile cards */}
            <div className="block lg:hidden space-y-4">
              {affiliations.map((row) => {
                const { initials, fullName } = deriveNameParts(row);
                return (
                  <Card
                    key={row.id}
                    className="rounded-2xl border border-slate-200/80 bg-white shadow-sm"
                  >
                    <CardContent className="space-y-4 p-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 shrink-0">
                          <ProfileAvatarImage
                            userId={row.userId}
                            profileImagePath={row.profileImagePath}
                          />
                          <AvatarFallback className="text-xs">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <Link
                            to="/org/bu/$organizationBusinessUnitId/provider-profile/$userId"
                            params={{
                              organizationBusinessUnitId:
                                selectedOrgBusinessUnit.id,
                              userId: row.userId,
                            }}
                            className="text-blue-600 hover:text-blue-700 font-semibold truncate block"
                          >
                            {fullName}
                          </Link>
                        </div>
                      </div>

                      {row.startDate && (
                        <div className="text-sm text-slate-600">
                          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
                            Started{" "}
                          </span>
                          {row.startDate}
                        </div>
                      )}

                      {canManage && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="self-start"
                          onClick={() =>
                            setEndTarget({
                              userId: row.userId,
                              name: fullName ?? "",
                            })
                          }
                        >
                          Mark not hired
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Desktop table */}
            <Card className="hidden lg:block p-0 border border-slate-200/80 shadow-sm overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
                    <TableHead className="w-12"></TableHead>
                    <TableHead className="min-w-[150px]">Provider</TableHead>
                    <TableHead className="min-w-[120px]">Start Date</TableHead>
                    <TableHead className="w-[160px] text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {affiliations.map((row) => {
                    const { initials, fullName } = deriveNameParts(row);
                    return (
                      <TableRow key={row.id} className="hover:bg-slate-50">
                        <TableCell className="py-3">
                          <Avatar className="h-8 w-8 shrink-0">
                            <ProfileAvatarImage
                              userId={row.userId}
                              profileImagePath={row.profileImagePath}
                            />
                            <AvatarFallback className="text-xs">
                              {initials}
                            </AvatarFallback>
                          </Avatar>
                        </TableCell>
                        <TableCell className="py-3">
                          <Link
                            to="/org/bu/$organizationBusinessUnitId/provider-profile/$userId"
                            params={{
                              organizationBusinessUnitId:
                                selectedOrgBusinessUnit.id,
                              userId: row.userId,
                            }}
                            className="text-blue-600 hover:text-blue-700 font-semibold"
                          >
                            {fullName}
                          </Link>
                        </TableCell>
                        <TableCell className="py-3 text-slate-600">
                          {row.startDate ?? "—"}
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          {canManage && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setEndTarget({
                                  userId: row.userId,
                                  name: fullName ?? "",
                                })
                              }
                            >
                              Mark not hired
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {/* Mark not hired dialog */}
        <MarkNotHiredDialog
          open={endTarget !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEndTarget(null);
            }
          }}
          providerName={endTarget?.name}
          onCancel={() => {
            setEndTarget(null);
          }}
          isPending={markNotHiredMutate.isPending}
          onConfirm={() => {
            if (!endTarget) {
              return;
            }
            markNotHiredMutate.mutate({
              organizationBusinessUnitId: selectedOrgBusinessUnit.id,
              userId: endTarget.userId,
            });
          }}
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/providers/affiliated/",
)({
  component: AffiliatedProvidersPage,
});
